package com.example.mystybag.appCore.logic.addMerchantLocation

import com.example.mystybag.appCore.model.addMerchantLocation.BusinessCategoryItem
import com.example.mystybag.appCore.model.addMerchantLocation.CatalogCategoryDraft
import com.example.mystybag.appCore.model.addMerchantLocation.CatalogEditorKind
import com.example.mystybag.appCore.model.addMerchantLocation.CatalogEditorState
import com.example.mystybag.appCore.model.addMerchantLocation.CatalogProductDraft
import com.example.mystybag.appCore.model.addMerchantLocation.LocationDetailsRequiredField
import com.example.mystybag.appCore.model.addMerchantLocation.MerchantLocationTab
import com.example.mystybag.appCore.model.addMerchantLocation.MerchantRegistrationAddMerchantLocationDetailsUIState
import com.example.mystybag.appCore.model.addMerchantLocation.ProductRowUIModel
import com.example.mystybag.common.resources.SR
import com.example.mystybag.communication.Communication
import com.example.mystybag.communication.common.CommonCommands
import com.example.mystybag.communication.defines.FileUploadTarget
import com.example.mystybag.communication.ext.CommandResult
import com.example.mystybag.communication.utils.Response
import com.example.mystybag.communication.utils.getOrNull
import com.example.mystybag.communication.utils.onResponse
import com.example.mystybag.core.expected.PlatformHelperWrapper.openGallery
import com.example.mystybag.core.expected.byteArrayFromFile
import com.example.mystybag.core.expected.getAddressFromLocation
import com.example.mystybag.core.expected.getLocateFromAddress
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.logic.base.AppContext
import com.example.mystybag.core.logic.base.ChildViewModel
import com.example.mystybag.core.logic.base.MainCallback
import com.example.mystybag.core.model.CityItem
import com.example.mystybag.core.model.CountryItem
import com.example.mystybag.cropImage.expected.getTempImageBytes
import com.example.mystybag.cropImage.logic.NavigatorProfileCropImage
import com.example.mystybag.cropImage.logic.ProfileImageCropperCallbacks
import com.example.mystybag.appCore.logic.NavigatorMerchantLocationsList
import com.example.mystybag.login.logic.NavigatorAddFirstBagPrompt
import com.example.mystybag.login.logic.NavigatorMerchantRegistrationStep7
import com.example.mystybag.sharedmodels.SCImage
import com.example.mystybag.sharedmodels.project.requestModels.merchant.DeleteProductCategoryRequestModel
import com.example.mystybag.sharedmodels.project.requestModels.merchant.DeleteProductRequestModel
import com.example.mystybag.sharedmodels.project.requestModels.merchant.GetLocationProductsRequestModel
import com.example.mystybag.sharedmodels.project.requestModels.merchant.SaveProductCategoryRequestModel
import com.example.mystybag.sharedmodels.project.requestModels.merchant.SaveProductRequestModel
import com.example.mystybag.sharedmodels.project.requestModels.nomenclatures.GetAllergensRequestModel
import com.example.mystybag.sharedmodels.project.responseModels.nomenclatures.GetAllergensResponseModel
import com.example.mystybag.sharedmodels.project.responseModels.merchant.parseLegacyProductsString
import com.example.mystybag.sharedmodels.project.requestModels.merchant.CreateMerchantLocationRequestModel
import com.example.mystybag.sharedmodels.project.requestModels.merchant.UpdateMerchantLocationRequestModel
import com.example.mystybag.sharedmodels.project.responseModels.merchant.CreateMerchantLocationResponseModel
import com.example.mystybag.sharedmodels.project.responseModels.merchant.GetLocationProductsResponseModel
import kotlin.math.abs
import kotlin.math.atan2
import kotlin.math.cos
import kotlin.math.PI
import kotlin.math.sin
import kotlin.math.sqrt
import kotlinx.coroutines.Job
import kotlinx.coroutines.launch

private const val DEFAULT_MAP_LATITUDE = 43.2141
private const val DEFAULT_MAP_LONGITUDE = 27.9147

class MerchantRegistrationAddMerchantLocationDetailsViewModel(
    context: AppContext,
    private val config: NavigatorMerchantRegistrationStep7
) : ChildViewModel<MerchantRegistrationAddMerchantLocationDetailsUIState>(
    context,
    MerchantRegistrationAddMerchantLocationDetailsUIState()
) {
    private var mapReverseGeocodeJob: Job? = null
    /** True after the user moves the pin on the map this session (not set from initial edit pre-fill). */
    private var mapPinChangedThisSession: Boolean = false

    /** Monotonic source of client-only catalog ids (categories + products share the space). */
    private var nextCatalogLocalId: Long = 1L

    private fun allocateCatalogLocalId(): Long = nextCatalogLocalId++

    override fun onCreate() {
        mapPinChangedThisSession = false
        navControls.backButtonVisible = true
        navControls.title = if (config.locationId != null) {
            sharedString(SR.strings.edit_merchant_location_title)
        } else {
            sharedString(SR.strings.add_store_screen_title)
        }
        updateStateFromConfig()
        restoreAccordionFromSavedConfig()
        loadCities()
        loadCountries()
        loadBusinessCategories()
        loadLocationFromAddress()
        loadAllergens()
        loadCatalogDraft()
    }

    /** Loads the allergen nomenclature used by the per-product allergen picker. */
    private fun loadAllergens() {
        scope.launch {
            commandBlock(showProgress = false) {
                CommonCommands.getAllergens(GetAllergensRequestModel(businessCategoryId = null))
                    .onResponse(
                        success = { response ->
                            val allergens = (response as? GetAllergensResponseModel)?.allergens ?: emptyList()
                            launchOnMain { updateState { it.copy(availableAllergens = allergens) } }
                        },
                        baseError = { }
                    )
            }
        }
    }

    override fun onPause() {
        super.onPause()
        val s = uiState.value
        config.savedStep7ExpandedTab = s.expandedTab?.name
        config.savedStep7SelectedTab = s.selectedTab.name
    }

    // region Product catalog (local draft tree)

    /** Loads the existing catalog into the local draft tree when editing a saved location. */
    private fun loadCatalogDraft() {
        val locationId = config.locationId ?: return
        scope.launch {
            commandBlock(showProgress = false) {
                CommonCommands.getLocationProducts(GetLocationProductsRequestModel(merchantLocationId = locationId))
                    .onResponse(
                        success = { response ->
                            val data = response as? GetLocationProductsResponseModel
                            val serverCategories = data?.categories ?: emptyList()
                            val serverProducts = data?.products ?: emptyList()
                            // Map each server category id to a freshly allocated localId.
                            val serverIdToLocalId = HashMap<Long, Long>()
                            val categoryDrafts = serverCategories.map { cat ->
                                val localId = allocateCatalogLocalId()
                                serverIdToLocalId[cat.id] = localId
                                Triple(localId, cat, cat.parentCategoryId)
                            }.map { (localId, cat, parentServerId) ->
                                CatalogCategoryDraft(
                                    localId = localId,
                                    serverId = cat.id,
                                    parentLocalId = parentServerId?.let { serverIdToLocalId[it] },
                                    name = cat.name,
                                    sortOrder = cat.sortOrder
                                )
                            }
                            val productDrafts = serverProducts.mapNotNull { product ->
                                val categoryLocalId = product.categoryId?.let { serverIdToLocalId[it] }
                                    ?: return@mapNotNull null
                                CatalogProductDraft(
                                    localId = allocateCatalogLocalId(),
                                    serverId = product.id,
                                    categoryLocalId = categoryLocalId,
                                    name = product.name,
                                    grams = product.grams,
                                    description = product.description,
                                    isEnabled = product.isEnabled,
                                    sortOrder = product.sortOrder,
                                    allergenIds = product.allergenIds
                                )
                            }
                            launchOnMain {
                                updateState {
                                    it.copy(
                                        catalogCategories = categoryDrafts,
                                        catalogProducts = productDrafts,
                                        // Expand root categories by default for visibility.
                                        catalogExpandedLocalIds = categoryDrafts
                                            .filter { c -> c.parentLocalId == null }
                                            .map { c -> c.localId }
                                            .toSet()
                                    )
                                }
                            }
                        },
                        baseError = { }
                    )
            }
        }
    }

    fun catalogToggleExpanded(localId: Long) = updateState {
        val next = if (localId in it.catalogExpandedLocalIds) {
            it.catalogExpandedLocalIds - localId
        } else {
            it.catalogExpandedLocalIds + localId
        }
        it.copy(catalogExpandedLocalIds = next)
    }

    fun catalogStartAddCategory(parentLocalId: Long?) {
        updateState {
            it.copy(
                catalogEditor = CatalogEditorState(
                    kind = CatalogEditorKind.CATEGORY,
                    parentLocalId = parentLocalId,
                    editingLocalId = null
                ),
                catalogExpandedLocalIds = if (parentLocalId != null) {
                    it.catalogExpandedLocalIds + parentLocalId
                } else {
                    it.catalogExpandedLocalIds
                }
            )
        }
    }

    fun catalogStartEditCategory(localId: Long) {
        val category = uiState.value.catalogCategories.firstOrNull { it.localId == localId } ?: return
        updateState {
            it.copy(
                catalogEditor = CatalogEditorState(
                    kind = CatalogEditorKind.CATEGORY,
                    parentLocalId = category.parentLocalId,
                    editingLocalId = category.localId,
                    nameInput = category.name
                )
            )
        }
    }

    fun catalogStartAddProduct(categoryLocalId: Long) {
        updateState {
            it.copy(
                catalogEditor = CatalogEditorState(
                    kind = CatalogEditorKind.PRODUCT,
                    parentLocalId = categoryLocalId,
                    editingLocalId = null
                ),
                catalogExpandedLocalIds = it.catalogExpandedLocalIds + categoryLocalId
            )
        }
    }

    fun catalogStartEditProduct(localId: Long) {
        val product = uiState.value.catalogProducts.firstOrNull { it.localId == localId } ?: return
        updateState {
            it.copy(
                catalogEditor = CatalogEditorState(
                    kind = CatalogEditorKind.PRODUCT,
                    parentLocalId = product.categoryLocalId,
                    editingLocalId = product.localId,
                    nameInput = product.name,
                    gramsInput = if (product.grams > 0) product.grams.toString() else "",
                    selectedAllergenIds = product.allergenIds.toSet()
                )
            )
        }
    }

    fun catalogToggleEditorAllergen(allergenId: Long) = updateState {
        val editor = it.catalogEditor ?: return@updateState it
        val next = if (allergenId in editor.selectedAllergenIds) {
            editor.selectedAllergenIds - allergenId
        } else {
            editor.selectedAllergenIds + allergenId
        }
        it.copy(catalogEditor = editor.copy(selectedAllergenIds = next))
    }

    fun catalogUpdateEditorName(value: String) = updateState {
        val editor = it.catalogEditor ?: return@updateState it
        it.copy(catalogEditor = editor.copy(nameInput = value.take(80), nameError = false))
    }

    fun catalogUpdateEditorGrams(value: String) = updateState {
        val editor = it.catalogEditor ?: return@updateState it
        it.copy(catalogEditor = editor.copy(gramsInput = value.filter { ch -> ch.isDigit() }.take(6), gramsError = false))
    }

    fun catalogCancelEditor() = updateState { it.copy(catalogEditor = null) }

    fun catalogSaveEditor() {
        val state = uiState.value
        val editor = state.catalogEditor ?: return
        val name = editor.nameInput.trim()
        if (name.isEmpty()) {
            updateState { it.copy(catalogEditor = editor.copy(nameError = true)) }
            return
        }
        when (editor.kind) {
            CatalogEditorKind.CATEGORY -> {
                val editingId = editor.editingLocalId
                if (editingId != null) {
                    updateState {
                        it.copy(
                            catalogCategories = it.catalogCategories.map { c ->
                                if (c.localId == editingId) c.copy(name = name) else c
                            },
                            catalogEditor = null
                        )
                    }
                } else {
                    val newLocalId = allocateCatalogLocalId()
                    updateState {
                        it.copy(
                            catalogCategories = it.catalogCategories + CatalogCategoryDraft(
                                localId = newLocalId,
                                parentLocalId = editor.parentLocalId,
                                name = name,
                                sortOrder = it.catalogCategories.count { c -> c.parentLocalId == editor.parentLocalId }
                            ),
                            catalogExpandedLocalIds = it.catalogExpandedLocalIds + newLocalId,
                            catalogEditor = null
                        )
                    }
                }
            }
            CatalogEditorKind.PRODUCT -> {
                val grams = editor.gramsInput.toIntOrNull() ?: 0
                if (grams <= 0) {
                    updateState { it.copy(catalogEditor = editor.copy(gramsError = true)) }
                    return
                }
                val categoryLocalId = editor.parentLocalId ?: return
                val editingId = editor.editingLocalId
                val allergenIds = editor.selectedAllergenIds.toList()
                if (editingId != null) {
                    updateState {
                        it.copy(
                            catalogProducts = it.catalogProducts.map { p ->
                                if (p.localId == editingId) p.copy(name = name, grams = grams, allergenIds = allergenIds) else p
                            },
                            catalogEditor = null
                        )
                    }
                } else {
                    updateState {
                        it.copy(
                            catalogProducts = it.catalogProducts + CatalogProductDraft(
                                localId = allocateCatalogLocalId(),
                                categoryLocalId = categoryLocalId,
                                name = name,
                                grams = grams,
                                allergenIds = allergenIds,
                                sortOrder = it.catalogProducts.count { p -> p.categoryLocalId == categoryLocalId }
                            ),
                            catalogEditor = null
                        )
                    }
                }
            }
        }
    }

    fun catalogDeleteCategory(localId: Long) {
        val state = uiState.value
        // Collect the category and all of its descendants.
        val toRemove = mutableSetOf(localId)
        var changed = true
        while (changed) {
            changed = false
            state.catalogCategories.forEach { c ->
                if (c.parentLocalId != null && c.parentLocalId in toRemove && c.localId !in toRemove) {
                    toRemove += c.localId
                    changed = true
                }
            }
        }
        val removedCategoryServerIds = state.catalogCategories
            .filter { it.localId in toRemove }
            .mapNotNull { it.serverId }
        val removedProductServerIds = state.catalogProducts
            .filter { it.categoryLocalId in toRemove }
            .mapNotNull { it.serverId }
        updateState {
            it.copy(
                catalogCategories = it.catalogCategories.filter { c -> c.localId !in toRemove },
                catalogProducts = it.catalogProducts.filter { p -> p.categoryLocalId !in toRemove },
                catalogExpandedLocalIds = it.catalogExpandedLocalIds - toRemove,
                catalogDeletedCategoryServerIds = it.catalogDeletedCategoryServerIds + removedCategoryServerIds,
                catalogDeletedProductServerIds = it.catalogDeletedProductServerIds + removedProductServerIds,
                catalogEditor = null
            )
        }
    }

    fun catalogDeleteProduct(localId: Long) {
        updateState {
            val product = it.catalogProducts.firstOrNull { p -> p.localId == localId }
            it.copy(
                catalogProducts = it.catalogProducts.filter { p -> p.localId != localId },
                catalogDeletedProductServerIds = it.catalogDeletedProductServerIds +
                    (product?.serverId?.let { id -> listOf(id) } ?: emptyList()),
                catalogEditor = null
            )
        }
    }

    /**
     * Persists the local catalog draft to the server for [locationId]:
     * deletes removed items, then upserts categories in parent-before-child
     * order (so [SaveProductCategoryRequestModel.parentCategoryId] resolves),
     * then upserts products with their resolved category id + grams.
     */
    private suspend fun syncCatalog(locationId: Long) {
        val state = uiState.value

        state.catalogDeletedProductServerIds.distinct().forEach { id ->
            CommonCommands.deleteProduct(DeleteProductRequestModel(productId = id))
        }
        state.catalogDeletedCategoryServerIds.distinct().forEach { id ->
            CommonCommands.deleteProductCategory(DeleteProductCategoryRequestModel(categoryId = id))
        }

        // localId -> resolved server id (seeded with already-known server ids).
        val resolvedIds = HashMap<Long, Long>()
        state.catalogCategories.forEach { c -> c.serverId?.let { resolvedIds[c.localId] = it } }

        val pending = state.catalogCategories.toMutableList()
        var progressed = true
        while (pending.isNotEmpty() && progressed) {
            progressed = false
            val iterator = pending.iterator()
            while (iterator.hasNext()) {
                val category = iterator.next()
                val parentServerId = when (val parentLocalId = category.parentLocalId) {
                    null -> null
                    else -> resolvedIds[parentLocalId] ?: continue // parent not resolved yet
                }
                val res = CommonCommands.saveProductCategory(
                    SaveProductCategoryRequestModel(
                        merchantLocationId = locationId,
                        id = category.serverId,
                        parentCategoryId = parentServerId,
                        name = category.name,
                        sortOrder = category.sortOrder
                    )
                )
                val savedId = res.getOrNull()?.id ?: category.serverId
                if (savedId != null) resolvedIds[category.localId] = savedId
                iterator.remove()
                progressed = true
            }
        }

        state.catalogProducts.forEach { product ->
            val categoryServerId = resolvedIds[product.categoryLocalId] ?: return@forEach
            CommonCommands.saveProduct(
                SaveProductRequestModel(
                    merchantLocationId = locationId,
                    id = product.serverId,
                    categoryId = categoryServerId,
                    name = product.name,
                    description = product.description,
                    grams = product.grams,
                    isEnabled = product.isEnabled,
                    sortOrder = product.sortOrder,
                    allergenIds = product.allergenIds
                )
            )
        }
    }

    // endregion

    private fun restoreAccordionFromSavedConfig() {
        val expanded = config.savedStep7ExpandedTab?.let { name ->
            runCatching { MerchantLocationTab.valueOf(name) }.getOrNull()
        }
        val selected = config.savedStep7SelectedTab?.let { name ->
            runCatching { MerchantLocationTab.valueOf(name) }.getOrNull()
        }
        if (expanded != null || selected != null) {
            updateState {
                it.copy(
                    expandedTab = expanded ?: it.expandedTab,
                    selectedTab = selected ?: it.selectedTab
                )
            }
        }
    }

    private fun updateStateFromConfig() {
        val hasSavedMapCoords = config.initialLatitude != null && config.initialLongitude != null
        updateState {
            it.copy(
                isEditMode = config.locationId != null,
                storeName = config.storeName,
                storeAddress = config.storeAddress,
                cityId = config.cityId,
                cityName = config.cityName,
                storePhoneNumber = "",
                dialCode = "",
                packaging = config.packaging,
                additionalPickupInstructions = config.additionalPickupInstructions,
                surpriseBagContainsBuffetFood = config.surpriseBagContainsBuffetFood,
                latitude = config.initialLatitude ?: it.latitude,
                longitude = config.initialLongitude ?: it.longitude,
                mapLocationConfirmedByUser = hasSavedMapCoords || it.mapLocationConfirmedByUser,
                selectedBusinessCategory = null,
                selectedPreviewImageIndex = null,
                productRows = parseWhatYouCouldGet(config.whatYouCouldGet),
                existingMenuPhotoUrls = config.existingMenuPhotoUrls.filter { url -> url.isNotBlank() }
            )
        }
    }

    private fun loadLocationFromAddress() {
        if (config.initialLatitude != null && config.initialLongitude != null) {
            updateState {
                it.copy(
                    latitude = config.initialLatitude,
                    longitude = config.initialLongitude,
                    mapLocationConfirmedByUser = true
                )
            }
            if (uiState.value.cityId == null) {
                selectNearestCityByCoordinates(config.initialLatitude!!, config.initialLongitude!!)
            }
            return
        }
        scope.launch {
            val fullAddress = "${config.storeAddress}, ${config.cityName}".trim().trim(',')
            val coordinates = when {
                fullAddress.isNotBlank() -> getLocateFromAddress(fullAddress)
                else -> null
            } ?: config.cityName.takeIf { it.isNotBlank() }?.let { getLocateFromAddress(it) }

            launchOnMain {
                if (coordinates != null) {
                    updateState {
                        it.copy(
                            latitude = coordinates.first,
                            longitude = coordinates.second,
                            mapLocationConfirmedByUser = true
                        )
                    }
                } else {
                    val city = uiState.value.cities.find { it.id == config.cityId }
                    val fromCity = city?.takeIf { c ->
                        abs(c.latitude) > 1e-6 || abs(c.longitude) > 1e-6
                    }?.let { it.latitude to it.longitude }
                    val (lat, lng) = fromCity ?: (DEFAULT_MAP_LATITUDE to DEFAULT_MAP_LONGITUDE)
                    updateState {
                        it.copy(
                            latitude = lat,
                            longitude = lng,
                            mapLocationConfirmedByUser = true
                        )
                    }
                }
                val s = uiState.value
                if (s.cityId == null && s.latitude != null && s.longitude != null) {
                    selectNearestCityByCoordinates(s.latitude!!, s.longitude!!)
                }
            }
        }
    }

    private fun loadCities() {
        scope.launch {
            commandBlock(showProgress = false) {
                CommonCommands.sendGetCities()
                    .onResponse(
                        success = { response ->
                            launchOnMain {
                                updateState {
                                    it.copy(
                                        cities = response.citiesList.map { city ->
                                            CityItem(
                                                id = city.id,
                                                name = city.cityName,
                                                countryId = city.countryId,
                                                latitude = city.latitude,
                                                longitude = city.longitude
                                            )
                                        }
                                    )
                                }
                                val currentAddress = uiState.value.storeAddress
                                if (currentAddress.isNotBlank()) {
                                    autoSelectCityFromAddress(currentAddress)
                                }
                                val s = uiState.value
                                if (s.cityId == null && s.latitude != null && s.longitude != null) {
                                    selectNearestCityByCoordinates(s.latitude!!, s.longitude!!)
                                }
                            }
                        }
                    )
            }
        }
    }

    private fun loadCountries() {
        scope.launch {
            commandBlock(showProgress = false) {
                CommonCommands.sendGetCountries()
                    .onResponse(
                        success = { response ->
                            launchOnMain {
                                updateState {
                                    it.copy(
                                        countries = response.countriesList?.map { country ->
                                            CountryItem(
                                                id = country.id,
                                                name = country.name,
                                                dialCode = country.dialCode,
                                                isoAlpha2 = country.isoAlpha2
                                            )
                                        } ?: emptyList()
                                    )
                                }
                            }
                        }
                    )
            }
        }
    }

    private fun loadBusinessCategories() {
        scope.launch {
            commandBlock(showProgress = false) {
                CommonCommands.getBusinessCategories(com.example.mystybag.sharedmodels.project.requestModels.merchant.GetBusinessCategoriesRequestModel())
                    .onResponse(
                        success = { response ->
                            launchOnMain {
                                val categories = response.categories
                                    .distinctBy { it.id }
                                    .map { cat ->
                                        BusinessCategoryItem(
                                            id = cat.id,
                                            title = cat.title,
                                            description = cat.description,
                                            coverImageUrls = cat.coverImageUrls.filter { it.isNotBlank() }
                                        )
                                    }
                                val selected = config.businessCategoryId?.let { id -> categories.find { it.id == id } }
                                updateState {
                                    val newSelected = it.selectedBusinessCategory ?: selected
                                    val matchedPreset = indexOfMatchingCoverPreset(config.existingCoverPhotoUrl, newSelected)
                                    it.copy(
                                        businessCategories = categories,
                                        // Prefer user selection if they picked before this request finished (avoid race with config pre-fill).
                                        selectedBusinessCategory = newSelected,
                                        selectedPreviewImageIndex = matchedPreset ?: it.selectedPreviewImageIndex
                                    )
                                }
                            }
                        }
                    )
            }
        }
    }

    fun selectTab(tab: MerchantLocationTab) {
        updateState { it.copy(selectedTab = tab, expandedTab = tab) }
    }

    fun toggleTab(tab: MerchantLocationTab) {
        updateState { current ->
            val nextExpanded = if (current.expandedTab == tab) null else tab
            current.copy(selectedTab = tab, expandedTab = nextExpanded)
        }
    }

    fun updateStoreName(name: String) {
        updateState { it.copy(storeName = name, requiredFieldErrors = it.requiredFieldErrors - LocationDetailsRequiredField.STORE_NAME) }
        config.storeName = name
    }

    fun updateStoreAddress(address: String) {
        updateState { it.copy(storeAddress = address) }
        config.storeAddress = address
    }

    fun showCityPicker() {
        updateState { it.copy(showCityPicker = true) }
    }

    fun hideCityPicker() {
        updateState { it.copy(showCityPicker = false) }
    }

    fun selectCity(cityId: Long, cityName: String) {
        val isNewLocation = config.locationId == null
        updateState {
            it.copy(
                cityId = cityId,
                cityName = cityName,
                showCityPicker = false,
                requiredFieldErrors = it.requiredFieldErrors - LocationDetailsRequiredField.CITY,
                mapLocationConfirmedByUser = if (isNewLocation) false else it.mapLocationConfirmedByUser,
                latitude = if (isNewLocation) null else it.latitude,
                longitude = if (isNewLocation) null else it.longitude
            )
        }
        config.cityId = cityId
        config.cityName = cityName
        if (isNewLocation) loadLocationFromAddress()
    }

    fun showCountryPicker() {
        updateState { it.copy(showCountryPicker = true) }
    }

    fun hideCountryPicker() {
        updateState { it.copy(showCountryPicker = false) }
    }

    fun selectCountry(countryId: Int, countryName: String, dialCode: String) {
        updateState {
            it.copy(
                dialCode = dialCode,
                showCountryPicker = false,
                requiredFieldErrors = it.requiredFieldErrors - LocationDetailsRequiredField.DIAL_CODE
            )
        }
        config.dialCode = dialCode
        config.countryId = countryId
        config.countryName = countryName
    }

    fun updateStorePhoneNumber(phone: String) {
        val filteredPhone = phone.filter { it.isDigit() }
        updateState { it.copy(storePhoneNumber = filteredPhone, requiredFieldErrors = it.requiredFieldErrors - LocationDetailsRequiredField.PHONE) }
        config.storePhoneNumber = filteredPhone
    }

    fun updatePackaging(value: String) {
        updateState { it.copy(packaging = value) }
        config.packaging = value
    }

    fun updateAdditionalPickupInstructions(value: String) {
        val trimmed = value.take(200)
        updateState { it.copy(additionalPickupInstructions = trimmed) }
        config.additionalPickupInstructions = trimmed
    }

    fun updateSurpriseBagContainsBuffetFood(checked: Boolean) {
        updateState { it.copy(surpriseBagContainsBuffetFood = checked) }
        config.surpriseBagContainsBuffetFood = checked
    }

    fun updateLocation(lat: Double, lng: Double) {
        mapPinChangedThisSession = true
        updateState {
            it.copy(
                latitude = lat,
                longitude = lng,
                mapLocationConfirmedByUser = true,
                requiredFieldErrors = it.requiredFieldErrors - LocationDetailsRequiredField.MAP_LOCATION
            )
        }
        mapReverseGeocodeJob?.cancel()
        mapReverseGeocodeJob = scope.launch {
            val address = getAddressFromLocation(lat, lng)?.trim().orEmpty()
            launchOnMain {
                if (uiState.value.latitude != lat || uiState.value.longitude != lng) return@launchOnMain
                if (address.isNotEmpty()) {
                    updateState { it.copy(storeAddress = address) }
                    config.storeAddress = address
                    autoSelectCityFromAddress(address)
                }
                if (uiState.value.cityId == null) {
                    selectNearestCityByCoordinates(lat, lng)
                }
            }
        }
    }

    fun onMapReady() {
        updateState { it.copy(isMapReady = true) }
    }

    fun showBusinessCategoryPicker() {
        updateState { it.copy(showBusinessCategoryPicker = true) }
    }

    fun hideBusinessCategoryPicker() {
        updateState { it.copy(showBusinessCategoryPicker = false) }
    }

    fun selectBusinessCategory(categoryId: Long, _categoryName: String) {
        val businessCategory = uiState.value.businessCategories.find { it.id == categoryId } ?: run {
            updateState { it.copy(showBusinessCategoryPicker = false) }
            return
        }
        val sameCategory = uiState.value.selectedBusinessCategory?.id == categoryId
        val nextPresetIndex = when {
            sameCategory -> uiState.value.selectedPreviewImageIndex
            else -> indexOfMatchingCoverPreset(config.existingCoverPhotoUrl, businessCategory)
        }
        updateState {
            it.copy(
                selectedBusinessCategory = businessCategory,
                selectedPreviewImageIndex = nextPresetIndex,
                showBusinessCategoryPicker = false,
                requiredFieldErrors = it.requiredFieldErrors -
                    LocationDetailsRequiredField.BUSINESS_CATEGORY -
                    LocationDetailsRequiredField.COVER_PREVIEW
            )
        }
    }

    fun selectPreviewImage(index: Int) {
        updateState {
            it.copy(
                selectedPreviewImageIndex = index,
                requiredFieldErrors = it.requiredFieldErrors - LocationDetailsRequiredField.COVER_PREVIEW
            )
        }
    }

    fun updateProductName(index: Int, text: String) {
        if (index !in uiState.value.productRows.indices) return
        val sanitized = sanitizeInputForEditing(text).take(80)
        updateState {
            it.copy(
                productRows = it.productRows.toMutableList().apply {
                    this[index] = this[index].copy(productName = sanitized)
                },
                requiredProductNameRows = it.requiredProductNameRows - index
            )
        }
    }

    fun updateGrams(index: Int, text: String) {
        if (index !in uiState.value.productRows.indices) return
        val grams = text.filter { char -> char.isDigit() }.take(6)
        updateState {
            it.copy(
                productRows = it.productRows.toMutableList().apply {
                    this[index] = this[index].copy(grams = grams)
                },
                requiredProductGramsRows = it.requiredProductGramsRows - index
            )
        }
    }

    fun addProductRow() {
        updateState { state ->
            if (state.productRows.size >= 20) state
            else state.copy(productRows = state.productRows + ProductRowUIModel())
        }
    }

    fun removeProductRow(index: Int) {
        updateState { state ->
            if (index !in state.productRows.indices || state.productRows.size <= 1) return@updateState state
            val updatedRows = state.productRows.filterIndexed { i, _ -> i != index }
            val updatedNameErrors = state.requiredProductNameRows.filter { it != index }.map { if (it > index) it - 1 else it }.toSet()
            val updatedGramsErrors = state.requiredProductGramsRows.filter { it != index }.map { if (it > index) it - 1 else it }.toSet()
            state.copy(productRows = updatedRows, requiredProductNameRows = updatedNameErrors, requiredProductGramsRows = updatedGramsErrors)
        }
    }

    fun onAddMenuPhotoClicked() {
        val state = uiState.value
        val existingCount = state.existingMenuPhotoUrls.size
        if (existingCount + state.menuPhotoFileNames.size >= MAX_MENU_PHOTOS) return
        scope.launch {
            val fileModel = openGallery()
            launchOnMain {
                fileModel?.image?.let { image -> cropMenuImage(image) }
            }
        }
    }

    private fun cropMenuImage(image: SCImage) {
        navigate(
            NavigatorProfileCropImage(
                selectedImage = image,
                callbacks = object : ProfileImageCropperCallbacks {
                    override fun onImageCropped(imageFileName: String, originalImgFileName: String) {
                        goBack()
                        updateState { current ->
                            if (current.menuPhotoFileNames.size >= MAX_MENU_PHOTOS) current
                            else current.copy(menuPhotoFileNames = current.menuPhotoFileNames + imageFileName)
                        }
                    }
                },
                aspectRatio = MENU_PHOTO_ASPECT_RATIO,
                outputWidth = MENU_PHOTO_OUTPUT_WIDTH,
                outputHeight = MENU_PHOTO_OUTPUT_HEIGHT,
            )
        )
    }

    fun removeMenuPhotoAt(index: Int) {
        updateState { current ->
            if (index !in current.menuPhotoFileNames.indices) current
            else current.copy(menuPhotoFileNames = current.menuPhotoFileNames.filterIndexed { i, _ -> i != index })
        }
    }

    fun removeExistingMenuPhotoAt(index: Int) {
        updateState { current ->
            if (index !in current.existingMenuPhotoUrls.indices) current
            else current.copy(existingMenuPhotoUrls = current.existingMenuPhotoUrls.filterIndexed { i, _ -> i != index })
        }
    }

    fun onContinue() {
        val state = uiState.value

        val missingFields = mutableSetOf<LocationDetailsRequiredField>()
        if (state.storeName.isBlank()) missingFields += LocationDetailsRequiredField.STORE_NAME
        if (state.cityId == null) missingFields += LocationDetailsRequiredField.CITY
        if (state.selectedBusinessCategory == null) missingFields += LocationDetailsRequiredField.BUSINESS_CATEGORY
        if (!state.mapLocationConfirmedByUser || state.latitude == null || state.longitude == null) {
            missingFields += LocationDetailsRequiredField.MAP_LOCATION
        }

        if (config.existingCoverPhotoUrl.isNullOrBlank()) {
            val presetUrls = state.selectedBusinessCategory?.coverImageUrls?.filter { it.isNotBlank() } ?: emptyList()
            val idx = state.selectedPreviewImageIndex
            if (presetUrls.isEmpty() || idx == null || idx !in presetUrls.indices) {
                missingFields += LocationDetailsRequiredField.COVER_PREVIEW
            }
        }

        if (missingFields.isNotEmpty()) {
            val targetTab = when {
                LocationDetailsRequiredField.STORE_NAME in missingFields -> MerchantLocationTab.BUSINESS
                LocationDetailsRequiredField.CITY in missingFields ||
                    LocationDetailsRequiredField.MAP_LOCATION in missingFields -> MerchantLocationTab.LOCATION
                else -> MerchantLocationTab.BUSINESS
            }
            updateState {
                it.copy(
                    requiredFieldErrors = missingFields,
                    requiredProductNameRows = emptySet(),
                    requiredProductGramsRows = emptySet(),
                    validationTargetTab = targetTab,
                    selectedTab = targetTab,
                    expandedTab = targetTab,
                    focusTargetField = missingFields.singleOrNull(),
                    validationRequestId = it.validationRequestId + 1
                )
            }
            val onlyMapMissing = LocationDetailsRequiredField.MAP_LOCATION in missingFields && missingFields.size == 1
            val onlyCityMissing = LocationDetailsRequiredField.CITY in missingFields && missingFields.size == 1
            val snack = when {
                onlyMapMissing -> sharedString(SR.strings.please_select_location_on_map)
                onlyCityMissing -> sharedString(SR.strings.please_select_city)
                else -> sharedString(SR.strings.please_enter_all_required_fields)
            }
            showSnackbar(snack)
            return
        }

        val businessCategory = state.selectedBusinessCategory ?: run {
            showSnackbar(sharedString(SR.strings.please_enter_all_required_fields))
            return
        }

        config.storeName = state.storeName
        config.storeAddress = state.storeAddress
        config.cityId = state.cityId
        config.cityName = state.cityName
        config.countryId = null
        config.countryName = ""
        config.storePhoneNumber = ""
        config.dialCode = ""
        config.packaging = state.packaging
        config.additionalPickupInstructions = state.additionalPickupInstructions
        config.surpriseBagContainsBuffetFood = state.surpriseBagContainsBuffetFood
        config.whatYouCouldGet = ""
        config.businessCategoryId = businessCategory.id
        submitLocation(state)
    }

    private fun autoSelectCityFromAddress(address: String) {
        val cities = uiState.value.cities
        if (cities.isEmpty()) return
        val matchedCity = findCityByAddress(address, cities) ?: return
        val currentState = uiState.value
        if (currentState.cityId == matchedCity.id && currentState.cityName == matchedCity.name) return
        updateState {
            it.copy(
                cityId = matchedCity.id,
                cityName = matchedCity.name,
                requiredFieldErrors = it.requiredFieldErrors - LocationDetailsRequiredField.CITY
            )
        }
        config.cityId = matchedCity.id
        config.cityName = matchedCity.name
    }

    /** When address text does not match any API city name, infer city from pin coordinates (only if [cityId] is still null). */
    private fun selectNearestCityByCoordinates(lat: Double, lng: Double) {
        if (uiState.value.cityId != null) return
        val cities = uiState.value.cities.filter { c ->
            abs(c.latitude) > 1e-6 || abs(c.longitude) > 1e-6
        }
        if (cities.isEmpty()) return
        val best = cities.minByOrNull { c -> haversineMeters(lat, lng, c.latitude, c.longitude) } ?: return
        if (uiState.value.cityId != null) return
        updateState {
            it.copy(
                cityId = best.id,
                cityName = best.name,
                requiredFieldErrors = it.requiredFieldErrors - LocationDetailsRequiredField.CITY
            )
        }
        config.cityId = best.id
        config.cityName = best.name
    }

    private fun haversineMeters(lat1: Double, lon1: Double, lat2: Double, lon2: Double): Double {
        val earthRadiusM = 6371000.0
        val dLat = (lat2 - lat1) * PI / 180.0
        val dLon = (lon2 - lon1) * PI / 180.0
        val radLat1 = lat1 * PI / 180.0
        val radLat2 = lat2 * PI / 180.0
        val h = sin(dLat / 2.0) * sin(dLat / 2.0) +
            cos(radLat1) * cos(radLat2) * sin(dLon / 2.0) * sin(dLon / 2.0)
        val hSafe = h.coerceIn(0.0, 1.0)
        val c = 2.0 * atan2(sqrt(hSafe), sqrt(1.0 - hSafe))
        return earthRadiusM * c
    }

    private fun findCityByAddress(address: String, cities: List<CityItem>): CityItem? {
        val normalizedAddress = normalizeCityToken(address)
        if (normalizedAddress.isBlank()) return null
        val addressTokens = normalizedAddress
            .split(",")
            .map { it.trim() }
            .filter { it.isNotBlank() }
        val normalizedCities = cities.map { city -> city to normalizeCityToken(city.name) }

        normalizedCities.firstOrNull { (_, cityName) ->
            addressTokens.any { token -> token == cityName }
        }?.let { return it.first }

        normalizedCities.firstOrNull { (_, cityName) ->
            addressTokens.any { token -> token.contains(cityName) || cityName.contains(token) }
        }?.let { return it.first }

        return normalizedCities.firstOrNull { (_, cityName) ->
            normalizedAddress.contains(cityName)
        }?.first
    }

    private fun normalizeCityToken(value: String): String = value
        .lowercase()
        .replace(".", " ")
        .replace("-", " ")
        .replace(Regex("\\s+"), " ")
        .trim()

    private fun parseWhatYouCouldGet(rawValue: String): List<ProductRowUIModel> {
        if (rawValue.isBlank()) return listOf(ProductRowUIModel())
        val parsedRows = rawValue
            .split(";")
            .mapNotNull { token ->
                val cleanedToken = token.trim()
                if (cleanedToken.isBlank()) return@mapNotNull null
                val parts = cleanedToken.split(",", limit = 2)
                val name = parts.getOrNull(0)?.trim().orEmpty()
                val grams = parts.getOrNull(1)?.trim().orEmpty().filter { it.isDigit() }
                ProductRowUIModel(productName = sanitizeInputForPayload(name).take(80), grams = grams.take(6))
            }
            .filter { it.productName.isNotBlank() || it.grams.isNotBlank() }
        return if (parsedRows.isNotEmpty()) parsedRows else listOf(ProductRowUIModel())
    }

    private fun buildWhatYouCouldGetPayload(rows: List<ProductRowUIModel>): String =
        rows.mapNotNull { row ->
            val productName = sanitizeInputForPayload(row.productName)
            val grams = row.grams.filter { it.isDigit() }
            if (productName.isBlank() || grams.isBlank()) null else "$productName,$grams"
        }.joinToString(";")

    private fun sanitizeInputForEditing(value: String): String =
        value.replace(";", "").replace(",", "")

    private fun sanitizeInputForPayload(value: String): String =
        value.replace(";", "").replace(",", "").trim()

    private fun submitLocation(state: MerchantRegistrationAddMerchantLocationDetailsUIState) {
        val resolvedLat = state.latitude!!
        val resolvedLng = state.longitude!!
        scope.launch {
            updateState { it.copy(isLoading = true, errorMessage = null) }
            commandBlock(showProgress = true) {
                var storeAddressForApi = state.storeAddress
                if (mapPinChangedThisSession) {
                    val fromPin = getAddressFromLocation(resolvedLat, resolvedLng)?.trim()?.takeIf { it.isNotEmpty() }
                    if (fromPin != null) storeAddressForApi = fromPin
                }
                if (storeAddressForApi.isBlank()) {
                    storeAddressForApi = getAddressFromLocation(resolvedLat, resolvedLng)?.takeIf { it.isNotBlank() } ?: storeAddressForApi
                }
                storeAddressForApi = storeAddressForApi.trim()

                val presetUrls = state.selectedBusinessCategory?.coverImageUrls?.filter { it.isNotBlank() } ?: emptyList()
                val coverPhotoUrl: String? = state.selectedPreviewImageIndex
                    ?.takeIf { it in presetUrls.indices }
                    ?.let { presetUrls[it] }

                val menuPhotoUrlsToAdd = mutableListOf<String>()
                for ((index, fileName) in state.menuPhotoFileNames.withIndex()) {
                    val bytes = byteArrayFromFile(fileName) ?: getTempImageBytes(fileName)
                    if (bytes != null) {
                        val res = Communication.uploadFile(FileUploadTarget.MERCHANT_LOCATION_MENU_PHOTOS, bytes, "menu_$index.jpg")
                        when (res) {
                            is CommandResult.Success -> menuPhotoUrlsToAdd.add(res.successModel.url)
                            else -> {
                                val err = (res as? CommandResult.BaseError)?.baseErrorModel
                                launchOnMain {
                                    updateState { it.copy(isLoading = false, errorMessage = Response.getErrorMessage(err?.status ?: -1, err?.apiEndPoint ?: "", err?.statusMessage)) }
                                }
                                return@commandBlock
                            }
                        }
                    }
                }

                val businessCategoryId = state.selectedBusinessCategory?.id ?: run {
                    launchOnMain {
                        updateState { it.copy(isLoading = false) }
                        showSnackbar(sharedString(SR.strings.please_enter_all_required_fields))
                    }
                    return@commandBlock
                }
                val finalCoverUrlForUpdate = coverPhotoUrl ?: config.existingCoverPhotoUrl
                val locationId = config.locationId
                if (locationId != null) {
                    CommonCommands.updateMerchantLocation(
                        UpdateMerchantLocationRequestModel(
                            locationId = locationId,
                            businessCategoryId = businessCategoryId,
                            storeName = state.storeName,
                            storeAddress = storeAddressForApi,
                            cityId = state.cityId ?: 0L,
                            storePhoneNumber = "",
                            dialCode = "",
                            packaging = state.packaging,
                            additionalPickupInstructions = state.additionalPickupInstructions,
                            surpriseBagContainsBuffetFood = state.surpriseBagContainsBuffetFood,
                            latitude = resolvedLat,
                            longitude = resolvedLng,
                            // Products & categories are managed via the location catalog,
                            // never from this flow. Empty list = leave catalog untouched.
                            products = emptyList(),
                            coverPhotoUrl = finalCoverUrlForUpdate,
                            menuPhotoUrlsToKeep = state.existingMenuPhotoUrls.filter { it.isNotBlank() },
                            menuPhotoUrlsToAdd = menuPhotoUrlsToAdd
                        )
                    ).onResponse(
                        success = {
                            syncCatalog(locationId)
                            launchOnMain {
                                updateState { it.copy(isLoading = false) }
                                setResult<MainCallback> { popToMainAndNavigate(NavigatorMerchantLocationsList) }
                            }
                        },
                        baseError = { response ->
                            launchOnMain {
                                updateState { it.copy(isLoading = false, errorMessage = Response.getErrorMessage(response.status, response.apiEndPoint, response.statusMessage)) }
                            }
                        }
                    )
                } else {
                    val coverForCreate = coverPhotoUrl ?: config.existingCoverPhotoUrl
                    if (coverForCreate.isNullOrBlank()) {
                        launchOnMain {
                            updateState { it.copy(isLoading = false) }
                            showSnackbar(sharedString(SR.strings.please_select_business_cover_image))
                        }
                        return@commandBlock
                    }
                    CommonCommands.createMerchantLocation(
                        CreateMerchantLocationRequestModel(
                            merchantId = config.merchantId,
                            businessCategoryId = businessCategoryId,
                            storeName = state.storeName,
                            storeAddress = storeAddressForApi,
                            cityId = state.cityId ?: 0L,
                            storePhoneNumber = "",
                            dialCode = "",
                            packaging = state.packaging,
                            additionalPickupInstructions = state.additionalPickupInstructions,
                            surpriseBagContainsBuffetFood = state.surpriseBagContainsBuffetFood,
                            latitude = resolvedLat,
                            longitude = resolvedLng,
                            // New locations start with no products; they are added
                            // afterwards from the location catalog (edit mode).
                            products = emptyList(),
                            coverPhotoUrl = coverForCreate,
                            menuPhotoUrls = menuPhotoUrlsToAdd
                        )
                    ).onResponse(
                        success = { response ->
                            val model = response as? CreateMerchantLocationResponseModel
                            val newLocationId = model?.merchantLocationId ?: 0L
                            if (newLocationId > 0L) syncCatalog(newLocationId)
                            launchOnMain {
                                updateState { it.copy(isLoading = false) }
                                setResult<MainCallback> {
                                    popToMainAndNavigate(
                                        NavigatorAddFirstBagPrompt(
                                            merchantLocationId = newLocationId,
                                            businessCategoryId = state.selectedBusinessCategory?.id,
                                            returnToDashboard = false
                                        )
                                    )
                                }
                            }
                        },
                        baseError = { response ->
                            launchOnMain {
                                updateState { it.copy(isLoading = false, errorMessage = Response.getErrorMessage(response.status, response.apiEndPoint, response.statusMessage)) }
                            }
                        }
                    )
                }
            }
        }
    }
}

